/**
 * @format
 * @module lib/commercial/keytar/common/preview
 * @description Shared methods for displaying preview walls, that appear within the article content.
 * It also cuts off the article content after a few paragraphs.
 */
import tgamConfig from "config/tgam";
import { removeDirtyChars } from "utils/utils";
import { toArray, qs, qsa } from "utils/utils";
import { isFunction } from "util";
import { hasLeadFigure } from "utils/story";

/**
 * Returns preview fade markup.
 * @param {String} color - background color for fade (e.g., $stone-150)
 * @returns {Boolean}
 */
function getFadeHtml(color = "white") {
  return `<div class="keytar-template-fade${
    color !== "white" ? ` keytar-template-fade--${color}` : ""
  }"></div>`;
}

/**
 * Determines if a node is an ad node
 * @param {Object} node - a DOM node
 * @returns {Boolean}
 */
function isAd(node) {
  return node.classList.contains("j-f-wrap");
}

/**
 * Removes a DOM node
 * @param {Object} node - a DOM node
 */
export function removeNode(node) {
  node.setAttribute("hidden", "hidden");
  node.parentNode.removeChild(node);
}

/**
 * Changes the text that appears above an inline advertisement
 * @param {Object} node - a DOM node
 * @param {String} [message="Advertisement"] - The new text to use
 */
function changeAdMessage(node, message = "Advertisement") {
  const adMessage = node.querySelector(".c-ad__message");
  if (adMessage) {
    adMessage.textContent = message;
  }
}

/**
 * Convert article content type string into value to be displayed in the UI.
 * @param {String} contentType - The type of content that the template is being rendered within
 * @returns {String} of content name
 */
export function contentName(contentType) {
  const types = {
    video: "video",
    gallery: "photo gallery",
    static: "content"
  };
  return types[contentType] || "article";
}

/**
 * Generate the analytics click event data object, to send via `wallAnalyticsSchema`.
 * @param {Object} c - collection or properties
 * @param {String} [c.feature=""] - The analytics feature, typically the paywall name
 * @param {String} [c.label=""] - The analytics label, typically the link text
 * @param {Number} [c.position=1] - The analytics position value that was clicked
 * @returns {{ type: String, feature: String, contentId: String, label: String, page: String, hierarchy: Number }}
 */
export function analyticsClickData({ feature = "", label = "", position = 1 }) {
  const sanitizedLabel = removeDirtyChars(label).trim().toLowerCase();
  return {
    type: "button",
    feature: feature,
    contentId: tgamConfig.datalayer.article.contentId,
    label: sanitizedLabel,
    page: tgamConfig.linkTracking.position,
    hierarchy: position
  };
}

/**
 * Add a click event handler to the rendered template. Captures and reports analytics clickEvent data.
 * @param {Object} wallAnalyticsSchema - an instance of the WallAnalyticsSchema class
 * @param {String} feature - The analytics feature, typically the paywall name
 * @param {Function} clickCallbackFn - callback function when a link has been clicked
 */
export function applyWallClickHandlers(wallAnalyticsSchema, feature, clickCallbackFn) {
  const template = document.querySelector(".preview-template");

  template.addEventListener("click", e => {
    e.preventDefault();
    const link = e.target.closest("a");
    if (!link) {
      // something other than a link was clicked, do nothing
      return;
    }
    let position = toArray(link.parentElement.children)
      .map(a => a === link)
      .indexOf(true);
    position = position >= 0 ? (position += 1) : null;
    const label = link.textContent || "";
    const data = analyticsClickData({ feature, label, position });

    // perhaps delete user cookies or set after login cookie via this callback
    clickCallbackFn(link);

    wallAnalyticsSchema.sendData(data, "clickEvent");
    // defer the load of the next page so that the event may be reported
    setTimeout(() => {
      window.location.href = link.href;
    }, 300);
  });
}

/**
 * Displays the teaser or preview (GA) paywall.
 * @param {Function|String} html - Callback function to render the template or the string template itself
 * @param {String} contentType - The content type name
 * @param {String} [wallName=""] - wall name type
 * @param {Object} [teaserWallConfigs={}] - collection or teaser wall configuration
 * @returns {Boolean} is true when successfully displayed
 */
export function defaultWall(html, contentType, wallName = "", teaserWallConfigs = {}) {
  const {
    isTeaserView = false,
    paragraphs = 0,
    contentBodySelector = ".js-c-article-body",
    fadeBgColor = "white"
  } = teaserWallConfigs;
  const contentBody = qs(contentBodySelector);

  if (!contentBody) {
    return false;
  }
  const config = { articleBody: contentBody, paragraphs, contentType };

  removeImagesAndInlineAds(config);

  const elementToInsertAfter = getElementToInsertPaywallAfter({ ...config, isTeaserView });
  const elementsToRemove = calculateElementsToRemove({ ...config, contentType, isTeaserView });
  // Teaser wall darwin scenario calls this function with html as a String
  const markup = isFunction(html) ? html(contentType) : html;
  const teaserWall = `
    <div class="c-keytar-container c-keytar-container--teaser">
      <div class="c-keytar-container__main js-c-teaser-body">${markup}</div>
    </div>
  `;
  if (paragraphs === 0 && wallName !== "GA") {
    contentBody.innerHTML = teaserWall;
  } else if (elementToInsertAfter) {
    elementToInsertAfter.style.position = "relative";
    elementToInsertAfter.insertAdjacentHTML("beforeend", getFadeHtml(fadeBgColor));
    elementToInsertAfter.insertAdjacentHTML("afterend", teaserWall);
    removeAdsAndRemainingNodes(elementsToRemove);
  }
  return true;
}

/**
 * Remove floated images and inline ads (if they are within first paragraphs).
 * @param {Object} c - collection of params.
 * @param {HTMLElement} c.articleBody
 * @param {Number} c.paragraphs
 */
function removeImagesAndInlineAds({ articleBody, paragraphs }) {
  // Check if any of the elements we insert wall after is a floated image
  // If yes, remove the element from the DOM and only then calculate the dom elements
  const firstTwoElements = [...articleBody.children].slice(0, 2);
  const elWithImage = firstTwoElements.find(el => el.classList.contains("c-inline-img--float"));
  elWithImage && elWithImage.parentElement.removeChild(elWithImage);
  // Check if the last visible element is an ad
  // If yes - remove the ad from the dom so it isn't calculated towards dom elements
  // Sometimes the slimcut ad appears after the second paragraph and get counted, but then it
  // disappears, so the fade class applied to it will be missing (as the element got removed)
  const lastVisibleElement = articleBody.children[paragraphs - 1];
  const lastVisibleElementIsAd =
    lastVisibleElement && lastVisibleElement.classList.contains("j-f-wrap");
  if (lastVisibleElementIsAd) {
    lastVisibleElement.parentElement.removeChild(lastVisibleElement);
  }
}

/**
 * Get the element we want to insert the wall after.
 * @param {Object} c - collection of params
 * @param {HTMLElement} c.articleBody - the whole article
 * @param {Boolean} c.isTeaserView - true if teaser view, not preview view (globe advisor)
 * @param {Number} c.paragraphs - number of paragraphs to insert the wall after
 * @returns {HTMLElement} - to insert wall after
 */
function getElementToInsertPaywallAfter({ articleBody, isTeaserView, paragraphs }) {
  // GA wall is the "Preview" wall that gets inserted after the first paragraph
  const previewWallInsertAfter = articleBody.children[0];
  // Teaser wall is all our regular walls (reg, red and metered) displayed in teaser mode
  const teaserWallInsertAfter = articleBody.children[paragraphs - 1];
  // Last dom element after which we are going to insert the wall
  return isTeaserView ? teaserWallInsertAfter : previewWallInsertAfter;
}

/**
 * Get dom elements that need to be removed.
 * @param {Object} c - collection of params
 * @param {HTMLElement} c.articleBody - the whole article
 * @param {String} c.contentType - the content type name
 * @param {Boolean} c.isTeaserView - true if teaser view, not preview view (globe advisor)
 * @param {Number} c.paragraphs - number of paragraphs to insert the wall after
 * @returns {HTMLElement} - dom elements to remove
 */
function calculateElementsToRemove({ articleBody, contentType, isTeaserView, paragraphs }) {
  const isLongreadWithLeadImg = contentType === "longread" && hasLeadFigure();
  const isGallery = contentType === "gallery";
  let elementsToRemove = [...articleBody.children];

  if (isGallery && window.location.hash) {
    // prevent the modal gallery from displaying since it will run after Keytar init has run
    window.location.hash = "";
  }

  if (!isGallery && !isLongreadWithLeadImg) {
    // For non-gallery and non-leading image longreads, remove all but the specified number of elements (first one for GA walls)
    elementsToRemove = !isTeaserView
      ? elementsToRemove.slice(1)
      : elementsToRemove.slice(paragraphs);
  }

  return elementsToRemove;
}

/**
 * Remove ads and other dom elements.
 * @param {Array} elementsToRemove
 */
function removeAdsAndRemainingNodes(elementsToRemove) {
  const rightRailAds = qsa("#secondary-content-area .c-ad");
  const adsToRemove = rightRailAds.slice(1);
  let adCounter = 0;
  elementsToRemove.forEach(node => {
    if (!isAd(node)) {
      removeNode(node);
      return;
    }
    adCounter = adCounter + 1;
    if (adCounter === 1) {
      changeAdMessage(node);
    } else {
      removeNode(node);
    }
  });
  adsToRemove.forEach(removeNode);
}

/**
 * Displays the paywall in place of the Video Center video player. Removes the video deck.
 * @param {Function|String} html - Callback function to render the template
 * @returns {Boolean} is true when successfully displayed
 */
export function videoWall(html) {
  const videoPlayer = document.querySelector(".c-video.powa");
  const videoMeta = document.querySelector(".c-video-info");

  if (!videoPlayer) {
    return false;
  }
  if (videoMeta) {
    videoMeta.parentNode.removeChild(videoMeta);
  }

  videoPlayer.insertAdjacentHTML("afterend", isFunction(html) ? html("video") : html);
  return true;
}

/**
 * Generate the HTML template for an preview paywall
 * @param {Object} c - collection or properties
 * @param {String} c.style - CSS classes for parent div element
 * @param {String} c.notice - of the top notice message, e.g. "To access this article ..."
 * @param {String} c.logo - HTML markup for a logo image or logo heading
 * @param {String} c.body - HTML markup for the main body content
 * @param {String} [c.prompt] - of the next question message, e.g. "Already a TGAM subscriber?"
 * @param {Object} [c.actions] - of UI links and buttons
 * @param {Object} [c.actions.cancel] - X button displayed at top-right
 * @param {Object} [c.actions.cancel.link] - href value for anchor
 * @param {Object} [c.actions.cancel.text] - display label for anchor
 * @param {Object} [c.actions.cancel.style] - CSS classes for anchor
 * @param {Object} [c.actions.login] - Login link displayed after prompt message
 * @param {Object} [c.actions.login.link] - href value for anchor
 * @param {Object} [c.actions.login.text] - display label for anchor
 * @param {Object} [c.actions.login.style] - CSS classes for anchor
 * @param {Object} [c.actions.primary] - Primary action button
 * @param {Object} [c.actions.primary.link] - href value for anchor
 * @param {Object} [c.actions.primary.text] - display label for anchor
 * @param {Object} [c.actions.primary.style] - CSS classes for anchor
 * @param {Object} [c.actions.secondary] - Secondary action button
 * @param {Object} [c.actions.secondary.link] - href value for anchor
 * @param {Object} [c.actions.secondary.text] - display label for anchor
 * @param {Object} [c.actions.secondary.style] - CSS classes for anchor
 * @param {Object} [c.actions.learn] - "Learn more" link below action buttons
 * @param {Object} [c.actions.learn.link] - href value for anchor
 * @param {Object} [c.actions.learn.text] - display label for anchor
 * @param {Object} [c.actions.learn.style] - CSS classes for anchor
 * @returns {String} of HTML markup to be inserted in the DOM
 */
export function previewTemplate({ style, notice, prompt, logo, body, actions }) {
  const login = actions.login
    ? `
  <a href="${actions.login.link}" class="${actions.login.style}">${actions.login.text}</a>`
    : "";

  const message = prompt
    ? `
  <div class="preview-template__head-login">
    <p>${prompt}${login}</p>
  </div>`
    : "";

  const primary = actions.primary
    ? `
  <a href="${actions.primary.link}" class="${actions.primary.style}">
    <span class="c-button__text">${actions.primary.text}</span>
  </a>`
    : "";

  const secondary = actions.secondary
    ? `
  <a href="${actions.secondary.link}" class="${actions.secondary.style}">
    <span class="c-button__text">${actions.secondary.text}</span>
  </a>`
    : "";

  const learn = actions.learn
    ? `
  <p><a href="${actions.learn.link}" class="${actions.learn.style}">${actions.learn.text}</a></p>`
    : "";

  return `
  <div class="preview-template ${style}">
    <div class="preview-template__head">
      <div class="preview-template__head-notice">
        <a href="${actions.cancel.link}" class="${actions.cancel.style}"
          title="${actions.cancel.text}" aria-label="${actions.cancel.text}">
          <span class="u-visually-hidden">${actions.cancel.text}</span>
          <svg class="c-button__icon" role="img" pointer-events="none" focusable="false" aria-hidden="true">
            <use xlink:href="${tgamConfig.env.svgSpritePath}#close-x"></use>
          </svg>
        </a>
        <p>${notice}</p>
      </div>
      ${message}
    </div>
    <div class="preview-template__body">
      <div class="preview-template__body-logo">${logo}</div>
      <div class="preview-template__body-copy">${body}</div>
      <div class="preview-template__body-buynow">${primary}${secondary}</div>
    </div>
    <div class="preview-template__foot">${learn}</div>
  </div>`;
}
